/**
 * Product Service
 * All product/model-related API endpoints
 */

import apiClient from './api/apiClient';
import { ENDPOINTS, buildEndpoint } from './api/endpoints';

/**
 * Product Service Object
 * Contains all methods for fetching and managing product data
 */
export const productService = {
  /**
   * Get Product Overview (Dashboard data)
   * Endpoint: GET /products/overview
   *
   * @param {Object} params - Query parameters
   * @param {number} params.limit_recent - Max recent views (1-20, default=8)
   * @param {number} params.limit_models - Max user models (1-200, default=30)
   *
   * Returns: {
   *   has_models: boolean,
   *   recent_views: Array<{
   *     id: number,
   *     name: string,
   *     description: string,
   *     image_url: string | null,
   *     is_demo: boolean,
   *     owner_id: number,
   *     last_price_value: number,
   *     last_price_currency: string,
   *     last_price_unit: string,
   *     last_price_date: string,
   *     updated_at: string
   *   }>,
   *   your_models: Array<...same structure...>,
   *   demo_models: Array<...same structure...>,
   *   related_fields: Array<{
   *     id: number,
   *     name: string,
   *     slug: string,
   *     image_url: string | null,
   *     product_count: number
   *   }>,
   *   all_sectors: Array<{
   *     id: number,
   *     name: string,
   *     slug: string,
   *     image_url: string | null,
   *     product_count: number
   *   }>
   * }
   */
  /**
   * List All Products (paginated)
   * Endpoint: GET /products
   *
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Page size (1-200, default=50)
   * @param {number} params.offset - Pagination offset (>=0, default=0)
   *
   * Returns: {
   *   items: Array<{
   *     id: number,
   *     name: string,
   *     description: string,
   *     image_url: string | null,
   *     is_demo: boolean,
   *     owner_id: number,
   *     last_price_value: number,
   *     last_price_currency: string,
   *     last_price_unit: string,
   *     last_price_date: string,
   *     updated_at: string
   *   }>,
   *   total: number,
   *   limit: number,
   *   offset: number,
   *   has_more: boolean
   * }
   */
  list: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.PRODUCTS.LIST, params);
    return apiClient.get(endpoint);
  },

  getOverview: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.PRODUCTS.OVERVIEW, params);
    return apiClient.get(endpoint);
  },

  /**
   * Get Recently Viewed Products
   * Endpoint: GET /products/me/recent_views
   *
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Number of products to return
   */
  getRecentViews: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.PRODUCTS.RECENT_VIEWS, params);
    return apiClient.get(endpoint);
  },

  /**
   * Get Related Sectors for User
   * Endpoint: GET /products/me/related_sectors
   *
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max sectors (1-100, default=12)
   *
   * Returns: Array<{
   *   id: number,
   *   name: string,
   *   slug: string,
   *   image_url: string | null,
   *   product_count: number
   * }>
   */
  getRelatedSectors: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.PRODUCTS.RELATED_SECTORS, params);
    return apiClient.get(endpoint);
  },

  /**
   * Get All Product Sectors with product counts
   * Endpoint: GET /products/sectors
   *
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max sectors (1-500, default=200)
   *
   * Returns: Array<{ id, name, slug, image_url, product_count }>
   */
  getSectors: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.PRODUCTS.SECTORS, params);
    return apiClient.get(endpoint);
  },

  /**
   * Get Products in a Sector
   * Endpoint: GET /products/sectors/{sectorId}
   *
   * @param {number} sectorId - Sector ID
   */
  getSectorProducts: async (sectorId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.SECTOR_PRODUCTS(sectorId));
  },

  /**
   * Get Sector Lists with Products
   * Endpoint: GET /products/sectors/{sectorId}/lists
   *
   * @param {number} sectorId - Sector ID
   */
  getSectorLists: async (sectorId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.SECTOR_LISTS(sectorId));
  },

  /**
   * Search Products by Name
   * Endpoint: GET /products/search
   *
   * @param {Object} params - Query parameters
   * @param {string} params.query - Search text (required)
   * @param {number} params.limit - Max results (1-200, default=50)
   *
   * Returns: { query, results: Array<{ product: { id, name, ... }, sectors, is_owner }> }
   */
  search: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.PRODUCTS.SEARCH, params);
    return apiClient.get(endpoint);
  },

  /**
   * Search Products for Selection (name + price fields only)
   * Endpoint: GET /products/search/options
   *
   * @param {Object} params - Query parameters
   * @param {string} params.query - Search text (required)
   * @param {number} params.limit - Max results (1-200, default=50)
   *
   * Returns: Array<{
   *   id: number,
   *   name: string,
   *   last_price_value: number,
   *   last_price_currency: string,
   *   last_price_unit: string
   * }>
   */
  searchOptions: async (params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.PRODUCTS.SEARCH_OPTIONS, params);
    return apiClient.get(endpoint);
  },

  /**
   * Prompt-style (AI) Product Search
   * Endpoint: POST /products/prompt
   *
   * @param {Object} data - Request body
   * @param {string} data.query - AI search query
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max results (1-200, default=50)
   *
   * Returns: { query, results: Array<{ product: { id, name, ... }, sectors, is_owner }> }
   */
  promptSearch: async (data, params = {}) => {
    const endpoint = buildEndpoint(ENDPOINTS.PRODUCTS.PROMPT_SEARCH, params);
    return apiClient.post(endpoint, data);
  },

  /**
   * Create a Product Model
   * Endpoint: POST /products
   *
   * @param {Object} data - Product data
   * @param {string} data.name - Product name
   * @param {string} data.description - Description
   * @param {string} data.image_url - Image URL (optional)
   * @param {string} data.default_currency - Currency (e.g., "EUR")
   * @param {string} data.base_unit - Unit (e.g., "unit", "kg")
   * @param {number} data.base_quantity - Base quantity (default=1)
   * @param {number[]} data.sector_ids - Sector IDs
   * @param {Object[]} data.components - Components with component_type, material_id/product_ref_id,
   *   name, quantity, unit, currency, source_country, destination_country, sort_order
   *
   * Returns: Full product object with id, sectors, components
   */
  create: async (data) => {
    return apiClient.post(ENDPOINTS.PRODUCTS.CREATE, data);
  },

  /**
   * Create a Product Model with Nested Sub-products
   * Endpoint: POST /products/nested
   *
   * @param {Object} data - Product data; components with component_type="product" can include
   *   a sub_product: { name, description, components: [...] } to create nested sub-products
   *
   * Returns: Full product object with id, sectors, components
   */
  createNested: async (data) => {
    return apiClient.post(ENDPOINTS.PRODUCTS.CREATE_NESTED, data);
  },

  /**
   * Get Product Detail
   * Endpoint: GET /products/{productId}
   *
   * @param {number} productId - Product ID
   */
  getDetail: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.DETAIL(productId));
  },

  /**
   * Update a Product Model (partial updates supported — send only changed fields)
   * Endpoint: PUT /products/{productId}
   *
   * @param {number} productId - Product ID
   * @param {Object} data - Fields to update; when components is provided it replaces the entire list
   *
   * Returns: Full updated product object
   */
  update: async (productId, data) => {
    return apiClient.put(ENDPOINTS.PRODUCTS.UPDATE(productId), data);
  },

  /**
   * Update a Product Model with Nested Sub-products
   * Endpoint: PUT /products/{productId}/nested
   *
   * @param {number} productId - Product ID
   * @param {Object} data - Updated data with sub_product objects inside components
   *
   * Returns: Full updated product object (same shape as GET /products/{id})
   */
  updateNested: async (productId, data) => {
    return apiClient.put(ENDPOINTS.PRODUCTS.UPDATE_NESTED(productId), data);
  },

  /**
   * Delete a Product Model (owner only)
   * Endpoint: DELETE /products/{productId}
   *
   * @param {number} productId - Product ID
   *
   * Returns: { ok: true }
   */
  delete: async (productId) => {
    return apiClient.delete(ENDPOINTS.PRODUCTS.DELETE(productId));
  },

  /**
   * Copy a Product Model into current user's workspace
   * Endpoint: POST /products/{productId}/copy
   *
   * @param {number} productId - Product ID to copy
   *
   * Returns: New product object with source_product_id pointing to original
   */
  copy: async (productId) => {
    return apiClient.post(ENDPOINTS.PRODUCTS.COPY(productId));
  },

  /**
   * Upload Product Image
   * Endpoint: POST /products/{productId}/image
   *
   * @param {number} productId - Product ID
   * @param {FormData} formData - Form data with field name "image_file"
   *
   * Returns: { id, name, image_url }
   */
  uploadImage: async (productId, formData) => {
    // Use request() directly to send FormData without JSON.stringify
    return apiClient.request(ENDPOINTS.PRODUCTS.IMAGE(productId), {
      method: 'POST',
      body: formData,
    });
  },

  /**
   * Get Core Product Info + Last Price
   * Endpoint: GET /products/{productId}/general
   *
   * @param {number} productId - Product ID
   *
   * Returns: {
   *   name: string,
   *   description: string,
   *   vat_percent: number | null,
   *   last_price_display: string (e.g., "12.950,25 EUR"),
   *   last_price_value: number,
   *   currency: string,
   *   value_unit: string,
   *   last_price_date_display: string (e.g., "Oct 12, 2025"),
   *   last_price_date_iso: string (e.g., "2025-10-12"),
   *   change_display: string | null,
   *   change_abs_value: number | null,
   *   change_pct_value: number | null
   * }
   */
  getGeneral: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.GENERAL(productId));
  },

  /**
   * Get Chart Overview (Monthly series)
   * Endpoint: GET /products/{productId}/chart/overview
   *
   * @param {number} productId - Product ID
   *
   * Returns: Array<{
   *   date: string (e.g., "2025-08-01"),
   *   monthLabel: string (e.g., "Aug 25"),
   *   value: number,
   *   timestamp: number (Unix ms)
   * }>
   */
  getChartOverview: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.CHART_OVERVIEW(productId));
  },

  /**
   * Get Chart Series (OHLC)
   * Endpoint: GET /products/{productId}/chart/series
   *
   * @param {number} productId - Product ID
   * @param {Object} params - Query parameters
   * @param {string} params.start - ISO date (required)
   * @param {string} params.end - ISO date (required)
   * @param {string} params.tf - Bucket size: 1d/1w/1m/1y (optional, default=1d)
   *
   * Returns: {
   *   data: Array<{
   *     date: string,
   *     open: number,
   *     high: number,
   *     low: number,
   *     close: number
   *   }>
   * }
   */
  getChartSeries: async (productId, params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.PRODUCTS.CHART_SERIES(productId),
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Get Chart Comparison
   * Endpoint: GET /products/{productId}/chart/compare
   *
   * @param {number} productId - Base product ID
   * @param {Object} params - Query parameters
   * @param {string} params.ids - Comma-separated product IDs to compare (required)
   * @param {string} params.start - ISO date (required)
   * @param {string} params.end - ISO date (required)
   * @param {string} params.tf - Bucket size: 1d/1w/1m/1y (optional, default=1d)
   *
   * Returns: {
   *   series: {
   *     [productId: string]: Array<{
   *       date: string,
   *       open: number,
   *       high: number,
   *       low: number,
   *       close: number
   *     }>
   *   }
   * }
   */
  getChartComparison: async (productId, params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.PRODUCTS.CHART_COMPARE(productId),
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Get Recent Daily Aggregates
   * Endpoint: GET /products/{productId}/recent_daily
   *
   * @param {number} productId - Product ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Max points (1-30, default=5)
   *
   * Returns: Array<{
   *   date: string (e.g., "12.10.2025"),
   *   change: number,
   *   price: number
   * }>
   */
  getRecentDaily: async (productId, params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.PRODUCTS.RECENT_DAILY(productId),
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Get Rolling Averages (7d/7w/7y)
   * Endpoint: GET /products/{productId}/rolling_averages
   *
   * @param {number} productId - Product ID
   *
   * Returns: {
   *   avg7d: number,
   *   avg7w: number,
   *   avg7y: number,
   *   counts: { d: number, w: number, y: number },
   *   end_date: string
   * }
   */
  getRollingAverages: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.ROLLING_AVERAGES(productId));
  },

  /**
   * Get Rolling Average Series (Bucketed)
   * Endpoint: GET /products/{productId}/rolling_average_series
   *
   * @param {number} productId - Product ID
   *
   * Returns: {
   *   days: Array<{ date: string, avg: number, count: number }>,
   *   weeks: Array<{ week: string, start: string, end: string, avg: number, count: number }>,
   *   months: Array<{ month: string, avg: number, count: number }>,
   *   years: Array<{ year: string, avg: number, count: number }>,
   *   end_date: string
   * }
   */
  getRollingAverageSeries: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.ROLLING_AVERAGE_SERIES(productId));
  },

  /**
   * Get Table Average Data
   * Endpoint: GET /products/{productId}/table_average
   *
   * @param {number} productId - Product ID
   * @param {Object} params - Query parameters
   * @param {number} params.limit - Table rows (1-100, default=12)
   * @param {number} params.weeks - Weekly buckets (1-26, default=8)
   * @param {number} params.months - Monthly buckets (1-24, default=8)
   * @param {number} params.years - Yearly buckets (1-10, default=5)
   *
   * Returns: {
   *   table: Array<{ date: string, change: number, price: number }>,
   *   weekly: Array<{ label: string, avg: number, start: string, end: string }>,
   *   monthly: Array<{ label: string, avg: number, start: string, end: string }>,
   *   yearly: Array<{ label: string, avg: number, start: string, end: string }>
   * }
   */
  getTableAverage: async (productId, params = {}) => {
    const endpoint = buildEndpoint(
      ENDPOINTS.PRODUCTS.TABLE_AVERAGE(productId),
      params
    );
    return apiClient.get(endpoint);
  },

  /**
   * Get Regulatory Entries
   * Endpoint: GET /products/{productId}/regulatory
   *
   * @param {number} productId - Product ID
   *
   * Returns: Array<{
   *   id: number,
   *   title: string,
   *   text: string,
   *   impact: string (e.g., "High", "Medium", "Low"),
   *   source_label: string,
   *   source_url: string,
   *   created_at: string (ISO datetime)
   * }>
   */
  getRegulatory: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.REGULATORY(productId));
  },

  /**
   * Create Regulatory Entry
   * Endpoint: POST /products/{productId}/regulatory
   *
   * @param {number} productId - Product ID
   * @param {Object} data - Regulatory entry data
   * @param {string} data.title - Entry title
   * @param {string} data.text - Entry text/description
   * @param {string} data.impact - Impact level (e.g., "High", "Medium", "Low")
   * @param {string} data.source_label - Source label
   * @param {string} data.source_url - Source URL
   *
   * Returns: { id, title, text, impact, source_label, source_url, created_at }
   */
  createRegulatory: async (productId, data) => {
    return apiClient.post(ENDPOINTS.PRODUCTS.REGULATORY(productId), data);
  },

  /**
   * Get Single Regulatory Entry
   * Endpoint: GET /products/{productId}/regulatory/{entryId}
   *
   * @param {number} productId - Product ID
   * @param {number} entryId - Entry ID
   *
   * Returns: {
   *   id: number,
   *   title: string,
   *   text: string,
   *   impact: string,
   *   source_label: string,
   *   source_url: string,
   *   created_at: string
   * }
   */
  getRegulatoryEntry: async (productId, entryId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.REGULATORY_ENTRY(productId, entryId));
  },

  /**
   * Update Regulatory Entry (partial update — send only changed fields)
   * Endpoint: PUT /products/{productId}/regulatory/{entryId}
   *
   * @param {number} productId - Product ID
   * @param {number} entryId - Entry ID
   * @param {Object} data - Fields to update (any subset of: title, text, impact, source_label, source_url)
   *
   * Returns: { id, title, text, impact, source_label, source_url, created_at }
   */
  updateRegulatory: async (productId, entryId, data) => {
    return apiClient.put(ENDPOINTS.PRODUCTS.REGULATORY_ENTRY(productId, entryId), data);
  },

  /**
   * Delete Regulatory Entry
   * Endpoint: DELETE /products/{productId}/regulatory/{entryId}
   *
   * @param {number} productId - Product ID
   * @param {number} entryId - Entry ID
   *
   * Returns: { ok: true }
   */
  deleteRegulatory: async (productId, entryId) => {
    return apiClient.delete(ENDPOINTS.PRODUCTS.REGULATORY_ENTRY(productId, entryId));
  },

  /**
   * Get Product Insights
   * Endpoint: GET /products/{productId}/insights
   *
   * @param {number} productId - Product ID
   *
   * Returns: Array<{
   *   id: number,
   *   title: string,
   *   text: string,
   *   source_label: string,
   *   source_url: string,
   *   published_at: string (ISO datetime),
   *   created_at: string (ISO datetime)
   * }>
   */
  getInsights: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.INSIGHTS(productId));
  },

  /**
   * Create Product Insight
   * Endpoint: POST /products/{productId}/insights
   *
   * @param {number} productId - Product ID
   * @param {Object} data - Insight data
   * @param {string} data.title - Insight title
   * @param {string} data.text - Insight text/description
   * @param {string} data.source_label - Source label
   * @param {string} data.source_url - Source URL
   * @param {string} data.published_at - ISO datetime of original publication
   *
   * Returns: { id, title, text, source_label, source_url, published_at, created_at }
   */
  createInsight: async (productId, data) => {
    return apiClient.post(ENDPOINTS.PRODUCTS.INSIGHTS(productId), data);
  },

  /**
   * Get Single Insight Entry
   * Endpoint: GET /products/{productId}/insights/{entryId}
   *
   * @param {number} productId - Product ID
   * @param {number} entryId - Entry ID
   *
   * Returns: {
   *   id: number,
   *   title: string,
   *   text: string,
   *   source_label: string,
   *   source_url: string,
   *   published_at: string (ISO datetime),
   *   created_at: string (ISO datetime)
   * }
   */
  getInsightEntry: async (productId, entryId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.INSIGHT_ENTRY(productId, entryId));
  },

  /**
   * Update Insight Entry (partial update — send only changed fields)
   * Endpoint: PUT /products/{productId}/insights/{entryId}
   *
   * @param {number} productId - Product ID
   * @param {number} entryId - Entry ID
   * @param {Object} data - Fields to update (any subset of: title, text, source_label, source_url, published_at)
   *
   * Returns: { id, title, text, source_label, source_url, published_at, created_at }
   */
  updateInsight: async (productId, entryId, data) => {
    return apiClient.put(ENDPOINTS.PRODUCTS.INSIGHT_ENTRY(productId, entryId), data);
  },

  /**
   * Delete Insight Entry
   * Endpoint: DELETE /products/{productId}/insights/{entryId}
   *
   * @param {number} productId - Product ID
   * @param {number} entryId - Entry ID
   *
   * Returns: { ok: true }
   */
  deleteInsight: async (productId, entryId) => {
    return apiClient.delete(ENDPOINTS.PRODUCTS.INSIGHT_ENTRY(productId, entryId));
  },

  /**
   * Get Classification (Sector breakdown for materials)
   * Endpoint: GET /products/{productId}/classification
   *
   * @param {number} productId - Product ID
   *
   * Returns: Array<{
   *   sector_id: number | null,
   *   sector_name: string,
   *   value: number,
   *   percent: number (0-100)
   * }>
   */
  getClassification: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.CLASSIFICATION(productId));
  },

  /**
   * Get Sources (Country breakdown for materials)
   * Endpoint: GET /products/{productId}/sources
   *
   * @param {number} productId - Product ID
   *
   * Returns: {
   *   countries: Array<{
   *     country: string,
   *     value: number,
   *     percent: number (0-100)
   *   }>
   * }
   */
  getSources: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.SOURCES(productId));
  },

  /**
   * Get Risk Assessment
   * Endpoint: GET /products/{productId}/risk
   *
   * @param {number} productId - Product ID
   *
   * Returns: {
   *   risk_score: number (0-100),
   *   risk_text: string
   * }
   */
  getRisk: async (productId) => {
    return apiClient.get(ENDPOINTS.PRODUCTS.RISK(productId));
  },

  /**
   * Upsert Risk Assessment (creates or updates)
   * Endpoint: PUT /products/{productId}/risk
   *
   * @param {number} productId - Product ID
   * @param {Object} data - Risk assessment data
   * @param {number} data.risk_score - Risk score (0-100)
   * @param {string} data.risk_text - Risk description text
   *
   * Returns: { risk_score: number, risk_text: string }
   */
  updateRisk: async (productId, data) => {
    return apiClient.put(ENDPOINTS.PRODUCTS.RISK(productId), data);
  },

  /**
   * Delete Risk Assessment
   * Endpoint: DELETE /products/{productId}/risk
   *
   * @param {number} productId - Product ID
   *
   * Returns: { ok: true }
   */
  deleteRisk: async (productId) => {
    return apiClient.delete(ENDPOINTS.PRODUCTS.RISK(productId));
  },
};

export default productService;
